/**
 * Azure External Entra ID Authentication Service
 * Implements OAuth 2.0 Authorization Code Flow with PKCE
 */

import { jwtDecode } from 'jwt-decode'
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  generateNonce,
  buildAuthorizationUrl,
  exchangeCodeForToken,
} from '../utils/oauth'

// Azure External Entra ID Configuration
const CLIENT_ID = process.env.REACT_APP_EXTERNAL_ID_CLIENT_ID || ''
const TENANT = process.env.REACT_APP_EXTERNAL_ID_TENANT || ''
const REDIRECT_URI = `${window.location.origin}/auth`

// Azure External Entra ID Endpoints
const AUTHORIZATION_ENDPOINT = `https://${TENANT}.ciamlogin.com/${TENANT}.onmicrosoft.com/oauth2/v2.0/authorize`
const TOKEN_ENDPOINT = `https://${TENANT}.ciamlogin.com/${TENANT}.onmicrosoft.com/oauth2/v2.0/token`
const LOGOUT_ENDPOINT = `https://${TENANT}.ciamlogin.com/${TENANT}.onmicrosoft.com/oauth2/v2.0/logout`

// OAuth Scopes
const SCOPES = 'openid profile email'

// SessionStorage Keys
// Note: Storing PKCE/OIDC parameters in sessionStorage is acceptable because:
// 1. They are single-use, short-lived values (cleaned up after callback)
// 2. CSP headers prevent XSS attacks that could read sessionStorage
// 3. They are not sensitive credentials (the actual token is stored in httpOnly cookies)
const STORAGE_KEYS = {
  CODE_VERIFIER: 'oauth_code_verifier',
  STATE: 'oauth_state',
  NONCE: 'oauth_nonce',
}

/**
 * Initiate login flow - redirects to Azure External Entra ID
 */
export async function login(): Promise<void> {
  if (!CLIENT_ID || !TENANT) {
    throw new Error('Azure External ID configuration missing. Please set REACT_APP_EXTERNAL_ID_CLIENT_ID and REACT_APP_EXTERNAL_ID_TENANT')
  }

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)
  const state = generateState()
  const nonce = generateNonce()

  // Store PKCE and OIDC parameters in sessionStorage for callback
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier)
  sessionStorage.setItem(STORAGE_KEYS.STATE, state)
  sessionStorage.setItem(STORAGE_KEYS.NONCE, nonce)

  // Build authorization URL and redirect
  const authUrl = buildAuthorizationUrl({
    authorizationEndpoint: AUTHORIZATION_ENDPOINT,
    clientId: CLIENT_ID,
    redirectUri: REDIRECT_URI,
    scope: SCOPES,
    state,
    codeChallenge,
    nonce,
    // Always show the sign-in page instead of silently reusing an existing Entra ID session
    prompt: 'login',
  })

  window.location.href = authUrl
}

/**
 * Handle OAuth callback - exchange authorization code for tokens
 * Supports both query string (?code=...) and hash fragment (#code=...) responses
 */
export async function handleCallback(): Promise<string> {
  // Azure can return OAuth params in query string (Web apps) or hash fragment (SPAs)
  // We support both for maximum compatibility
  const queryParams = new URLSearchParams(window.location.search)
  const hashParams = new URLSearchParams(window.location.hash.substring(1))

  // Try query params first (standard for auth code flow), fallback to hash
  const code = queryParams.get('code') || hashParams.get('code')
  const state = queryParams.get('state') || hashParams.get('state')
  const error = queryParams.get('error') || hashParams.get('error')
  const errorDescription = queryParams.get('error_description') || hashParams.get('error_description')

  // Retrieve stored PKCE and OIDC parameters early for cleanup
  const storedState = sessionStorage.getItem(STORAGE_KEYS.STATE)
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER)
  const storedNonce = sessionStorage.getItem(STORAGE_KEYS.NONCE)

  try {
    // Check for errors
    if (error) {
      throw new Error(`OAuth error: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`)
    }

    if (!code) {
      throw new Error('No authorization code received')
    }

    // Validate state parameter (CSRF protection)
    if (!storedState || state !== storedState) {
      throw new Error('Invalid state parameter - possible CSRF attack')
    }

    if (!codeVerifier) {
      throw new Error('Code verifier not found in session')
    }

    if (!storedNonce) {
      throw new Error('Nonce not found in session')
    }

    // Exchange code for tokens
    const tokenResponse = await exchangeCodeForToken({
      tokenEndpoint: TOKEN_ENDPOINT,
      clientId: CLIENT_ID,
      redirectUri: REDIRECT_URI,
      code,
      codeVerifier,
    })

    // Validate nonce in ID token (prevents token replay and mix-up attacks)
    try {
      const decodedToken = jwtDecode<{ nonce?: string }>(tokenResponse.id_token)

      if (!decodedToken.nonce) {
        throw new Error('Nonce not found in ID token')
      }

      if (decodedToken.nonce !== storedNonce) {
        throw new Error('Invalid nonce - possible token replay attack')
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes('nonce')) {
        throw err
      }
      throw new Error('Failed to decode and validate ID token')
    }

    // Return ID token
    return tokenResponse.id_token
  } finally {
    // Always clean up PKCE and OIDC parameters from storage, regardless of success or failure
    sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER)
    sessionStorage.removeItem(STORAGE_KEYS.STATE)
    sessionStorage.removeItem(STORAGE_KEYS.NONCE)
  }
}

/**
 * Logout - clear session and redirect to Azure logout endpoint
 */
export function logout(): void {
  // Build logout URL with post-logout redirect
  const logoutUrl = `${LOGOUT_ENDPOINT}?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`

  // Redirect to Azure logout
  window.location.href = logoutUrl
}
